import { startTransition, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { gameUrl } from '../lib/url-builders';
import { redirectTo, toGlpWonSuccess } from '../store/location/actions';
import { selectSuperchargerInviteToken } from '../store/session/selectors';

import { useCheckJoinability } from './use-check-joinability';

// Shared by every GLP Won signup step except GlpWonSuccess itself (see
// PhoneNumber/GlpWonPay for the original of this check). A player who's
// already joined - most commonly by re-opening their own invite link days
// later - should never be able to sit on any step of the signup funnel.
//
// "Already joined" is decided from userStatusV2.isPlayer, NOT from
// joinability.code - joinability runs a whole chain of eligibility checks
// (game_removed, past_deadline, already_playing, ...) and returns whichever
// one fires FIRST, so an already-joined player can just as easily get back
// e.g. "pastJoinDeadline" once the round's join window closes, silently
// hiding the fact that they're in. isPlayer is a direct, synchronous fact
// with no other reason that can mask it (same signal GlpWonSuccess itself
// re-checks - see useGlpWonSuccessGameQuery). joinable/joinability.code is
// still used for every OTHER blocked reason (sent back to the invite
// landing page instead - see the effect below; never the generic game
// directory, which says nothing about this specific round or why it
// stopped being joinable).
//
// For an anonymous visitor both signals only ever resolve their generic,
// caller-JWT-less form - isPlayer/joinability are per-caller, so a real
// already-joined status only surfaces once someone's identity exists.
export function useSuperchargerJoinabilityGuard({ gameId }) {
  const dispatch = useDispatch();
  const inviteToken = useSelector(selectSuperchargerInviteToken);
  const numericGameId = Number(gameId) || null;

  const {
    joinable,
    isPlayer,
    loading: joinabilityLoading,
  } = useCheckJoinability({ gameId: numericGameId, gameType: 'falkor' });
  // Keyed off joinable's presence, not isPlayer's - isPlayer is only ever
  // requested for an authenticated caller (see query.js's
  // @include(if: $isAuthenticated)), so it stays `undefined` - not `false` -
  // for an anonymous visitor, and would never resolve to a known boolean at
  // all otherwise, leaving this permanently "loading" for them.
  const joinabilityKnown =
    !!numericGameId && !joinabilityLoading && typeof joinable === 'boolean';
  // !!isPlayer treats "unauthenticated, so not requested" the same as
  // "authenticated and not a player" - both mean not already joined. A
  // player the game is still joinable for is a Supercharger trial player
  // (a free member, 2026-10-08): falkor lets her pay for her own game, so
  // she hasn't joined yet either.
  const alreadyJoined = joinabilityKnown && !!isPlayer && !joinable;
  const blocked = joinabilityKnown && (alreadyJoined || !joinable);

  // startTransition: can fire as soon as the joinability query resolves,
  // which can land within the first few frames on a fast network - see
  // GlpWonPay's own effects for the full hydration-race/React error #421
  // explanation.
  useEffect(() => {
    if (!blocked) return;

    startTransition(() => {
      if (alreadyJoined) {
        dispatch(toGlpWonSuccess({ gameId: numericGameId }));
        return;
      }

      // Sent back to the exact invite page this signup funnel started
      // from (see invite-landing.js, which sets this the moment it's
      // known) rather than the generic game directory - that page at
      // least explains what this specific game/round is, even though it
      // won't have a nicer "why you're seeing this again" message either
      // yet. Falls back to the game's own page in the (should be
      // unreachable in practice, since every real entry point sets the
      // token first) case where it's somehow missing.
      dispatch(
        redirectTo(
          inviteToken
            ? `/invite-glp-won/${inviteToken}`
            : gameUrl({ gameId: numericGameId })
        )
      );
    });
  }, [blocked, alreadyJoined, numericGameId, dispatch, inviteToken]);

  return { joinabilityKnown, blocked, isPlayer: !!isPlayer };
}
